import React from 'react'
import { Link } from 'react-router-dom'
import { Box, Paper, Typography, useMediaQuery } from '@mui/material'
import ROUTES from '../../routesConfig'
import {
  formatVenueScheduledDate,
  formatVenueScheduledTime
} from '../Utils/venueSchedulingTime'
import venueStateChipConfig from '../Utils/venueStateChipConfig'

const venueTileTextColor = {
  title: '#E9DCC9',
  label: '#90A4AE',
  value: 'rgba(230,233,239,0.84)'
}

const normalizeVenueEntry = (entry) => {
  if (!entry) return null
  if (entry.venue) {
    return {
      id: String(entry.id || entry.venue.id || ''),
      venue: entry.venue
    }
  }

  return {
    id: String(entry.id || ''),
    venue: entry
  }
}

const normalizeVenueState = (venue) => String(venue?.state || '').trim().toLowerCase()

const venueScheduleLabels = (venue, { compactDates = false } = {}) => {
  const scheduledAt = venue?.scheduledAt
  const timeZone = venue?.timeZone || undefined
  return {
    date: formatVenueScheduledDate({ scheduledAt, timeZone, fallback: 'TBD', compact: compactDates }),
    time: formatVenueScheduledTime({ scheduledAt, timeZone, fallback: 'TBD' })
  }
}

const VenueList = ({ playableVenues, title = 'Venues You Joined' }) => {
  const isNarrowPhone = useMediaQuery('(max-width:380px)')
  const venueEntries = (playableVenues || [])
    .map(normalizeVenueEntry)
    .filter((entry) => entry?.venue?.name)

  if (venueEntries.length === 0) return null

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h6"
        sx={{
          padding: '0.5rem 0',
          fontWeight: 500,
          color: 'rgba(230,233,239,0.8)',
          fontSize: '0.9rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {venueEntries.map(({ id, venue }) => {
          const normalizedState = normalizeVenueState(venue)
          const stateChip = venueStateChipConfig(normalizedState, { includeUnknown: false })
          const schedule = venueScheduleLabels(venue, { compactDates: isNarrowPhone })
          const photoUrl = venue.venuePhotoMediumUrl || venue.venuePhotoUrl || ''
          const showSchedule = Boolean(venue.scheduledAt)
          const mediaWidth = isNarrowPhone ? '5.7rem' : '6.9rem'

          return (
            <Link
              key={id}
              style={{ textDecoration: 'none', display: 'block', width: '100%' }}
              to={ROUTES.VENUE_PAGE(venue.slug || venue.id)}
            >
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.08)',
                  borderRadius: 2,
                  px: 1.05,
                  py: 0.72,
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
                  textDecoration: 'none',
                  transition: 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.12)',
                    borderColor: 'rgba(255,255,255,0.22)',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.34)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `${mediaWidth} minmax(0, 1fr)`,
                    alignItems: 'start',
                    columnGap: 0.95
                  }}
                >
                  {photoUrl ? (
                    <Box
                      component="img"
                      src={photoUrl}
                      alt={venue.name}
                      loading="lazy"
                      sx={{
                        width: mediaWidth,
                        height: mediaWidth,
                        borderRadius: 1.3,
                        objectFit: 'cover',
                        display: 'block',
                        border: '1px solid rgba(255,255,255,0.16)'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: mediaWidth,
                        height: mediaWidth,
                        borderRadius: 1.3,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      minWidth: 0,
                      pr: 0.1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        columnGap: 0.6,
                        alignItems: 'start'
                      }}
                    >
                      <Typography
                        sx={{
                          color: venueTileTextColor.title,
                          fontWeight: 800,
                          fontSize: { xs: isNarrowPhone ? '1rem' : '1.12rem', sm: '1.2rem' },
                          lineHeight: 1.1,
                          letterSpacing: '-0.01em',
                          textShadow: '0 1px 8px rgba(0,0,0,0.28)'
                        }}
                      >
                        {venue.name}
                      </Typography>
                      {stateChip && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 999,
                            px: 0.82,
                            py: 0.18,
                            fontSize: '0.7rem',
                            fontWeight: 800,
                            letterSpacing: '0.03em',
                            textTransform: 'uppercase',
                            color: stateChip.color,
                            background: stateChip.background,
                            border: `1px solid ${stateChip.borderColor}`,
                            whiteSpace: 'nowrap',
                            alignSelf: 'start',
                            mt: 0.1
                          }}
                        >
                          {stateChip.label}
                        </Box>
                      )}
                    </Box>

                    {showSchedule ? (
                      <Box sx={{ mt: 0.52, display: 'flex', flexDirection: 'column', gap: 0.28 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '3rem minmax(0, 1fr)', columnGap: 0.35, alignItems: 'baseline' }}>
                          <Typography sx={{ fontSize: '0.84rem', color: venueTileTextColor.label, fontWeight: 800 }}>
                            Date:
                          </Typography>
                          <Typography sx={{ fontSize: '0.88rem', color: venueTileTextColor.value, lineHeight: 1.3 }}>
                            {schedule.date}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '3rem minmax(0, 1fr)', columnGap: 0.35, alignItems: 'baseline' }}>
                          <Typography sx={{ fontSize: '0.84rem', color: venueTileTextColor.label, fontWeight: 800 }}>
                            Time:
                          </Typography>
                          <Typography sx={{ fontSize: '0.88rem', color: venueTileTextColor.value, lineHeight: 1.3 }}>
                            {schedule.time}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography sx={{ mt: 0.52, color: 'rgba(230,233,239,0.65)', fontSize: '0.85rem' }}>
                        View ongoing venue events
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Link>
          )
        })}
      </Box>
    </Box>
  )
}

export default VenueList
